import { useState, useEffect } from "react";
import { usePermissions } from "@/contexts/PermissionContext";
import { Button } from "@/components/ui/button";
import { User } from "@/types";
import {
  Pencil,
  Trash2,
  User as UserIcon,
  Mail,
  Phone,
  UserCog,
  Send,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatPhoneNumber } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/components/ui/use-toast";
import { getUserDisplayName } from "@/utils/userUtils";
import { effectiveRole } from "@/lib/effectiveRole";
import { passwordResetService } from "@/services/passwordResetService";
import { accessEmailToast } from "@/lib/welcomeEmailNotice";

type UserListProps = {
  users: User[];
  loading: boolean;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  currentUserId?: string;
  canEdit: boolean;
  canDelete: boolean;
  /** Admins who may reset passwords may also resend the welcome email. */
  canSendAccessEmails?: boolean;
  teamFilter?: string;
  searchQuery?: string;
  typeFilter?: string;
};

export const UserList: React.FC<UserListProps> = ({
  users,
  loading,
  onEdit,
  onDelete,
  currentUserId,
  canEdit,
  canDelete,
  canSendAccessEmails = false,
  teamFilter,
  searchQuery,
  typeFilter,
}) => {
  const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
  const { impersonateUser, user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const [sendingWelcomeTo, setSendingWelcomeTo] = useState<string | null>(null);
  
  const hasSuperadminAccess = hasPermission('system.override') || hasPermission('tenant.manage');

  const handleImpersonate = async (user: User) => {
    try {
      // Create a User object that matches the Supabase User type
      const userToImpersonate = {
        id: user.id,
        email: user.email,
        user_metadata: {
          first_name: user.first_name,
          last_name: user.last_name,
        },
        app_metadata: {
          role_id: user.role_id,
        },
      } as any; // Type assertion needed since we're creating a partial User object

      await impersonateUser(userToImpersonate);
      toast({
        title: "Impersonating User",
        description: `You are now impersonating ${getUserDisplayName(user)}`,
      });
    } catch (error) {
      console.error("Error impersonating user:", error);
      toast({
        title: "Error",
        description: "Failed to impersonate user",
        variant: "destructive",
      });
    }
  };

  const handleResendWelcome = async (user: User) => {
    if (!currentTenant?.tenant_id) return;
    setSendingWelcomeTo(user.id);
    try {
      const { emailSent } = await passwordResetService.sendWelcomeEmail(
        user.email,
        currentTenant.tenant_id,
      );
      toast(accessEmailToast("welcome", user.email, emailSent));
    } catch (error) {
      toast({
        title: "Welcome email not sent",
        description: error instanceof Error ? error.message : "Something went wrong. Try again.",
        variant: "destructive",
      });
    } finally {
      setSendingWelcomeTo(null);
    }
  };

  // Filter users based on team and search query
  useEffect(() => {
    let result = users;

    // Apply team filter
    if (teamFilter && teamFilter !== "all") {
      result = result.filter(
        (user) =>
          user.user_teams?.some((team) => team.team_id === teamFilter) || false
      );
    }

    // Apply search query
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(
        (user) =>
          getUserDisplayName(user)
            .toLowerCase()
            .includes(lowerQuery) ||
          user.email.toLowerCase().includes(lowerQuery)
      );
    }

    setFilteredUsers(result);
  }, [users, teamFilter, searchQuery]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="flex items-center space-x-4 p-4 border rounded-lg"
          >
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (filteredUsers.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg bg-muted/20">
        <UserIcon className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-2 text-lg font-medium text-foreground">
          No users found
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {teamFilter && teamFilter !== "all"
            ? "No users found for the selected team."
            : searchQuery
              ? "No users match your search criteria."
              : "Get started by creating a new user."}
        </p>
      </div>
    );
  }

  // Format user role
  const formatRole = (roleName?: string) => {
    if (!roleName) return "No Role";
    return roleName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  /**
   * The role this person holds IN the account being viewed.
   *
   * ⚠️ THIS CELL USED TO RENDER `formatRole(user.role_details?.name)`, which follows
   * the LEGACY `users.role_id`, and it was wrong for both members of the one
   * sub-account-enabled account, in opposite directions (measured 2026-08-13):
   *
   *   carlos+trippertenant@   shown "Admin"           actually Agency Admin here
   *   aglasier24@             shown "Agency Admin"    actually Superadmin here
   *
   * So an Agency Admin appeared as an ordinary Admin, which is what made SUB-3.1's
   * entry point look broken: the gate correctly admitted an Agency Admin that this
   * column was calling an Admin. `roles_in_tenant` comes from `user_roles` filtered
   * to the current account, and the legacy column is the fallback for people who
   * hold no row there. The rule lives in `@/lib/effectiveRole` with its tests.
   *
   * ⚠️ EACH NAME IS FORMATTED SEPARATELY, NOT THE JOINED LABEL. `formatRole`
   * lowercases everything after the first character, so handing it
   * "Admin, Sales Rep, Superadmin" returns "Admin, sales rep, superadmin". One live
   * user does hold exactly those three in a single tenant.
   */
  const roleCellText = (user: User) => {
    const { names } = effectiveRole({
      rolesInThisTenant: user.roles_in_tenant,
      legacyRoleName: user.role_details?.name,
    });
    // formatRole(undefined) is the existing "No Role" wording, kept rather than
    // reinvented so a user with neither source reads exactly as they do today.
    return names.length === 0
      ? formatRole(undefined)
      : names.map((n) => formatRole(n)).join(", ");
  };

  // Get sales teams for a user
  const getUserSalesTeams = (user: User) => {
    const userTeams = user.user_teams || [];
    const salesTeams = userTeams
      .filter((ut) => ut.teams?.name) // Only teams with names
      .map((ut) => ut.teams!.name);

    return salesTeams.length > 0 ? salesTeams.join(", ") : "N/A";
  };

  // Get installer teams for a user
  const getUserInstallerTeams = (user: User) => {
    const userInstallerPartners = user.installer_partners || [];
    return userInstallerPartners.length > 0
      ? userInstallerPartners.map((partner) => partner.company_name).join(", ")
      : "N/A";
  };

  return (
    <div className="border rounded-lg shadow-sm">
      <div className="overflow-x-auto">
        <Table className="w-full table-fixed">
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead className="w-[16%] font-semibold text-muted-foreground">
                Name
              </TableHead>
              <TableHead className="w-[18%] font-semibold text-muted-foreground">
                Email
              </TableHead>
              <TableHead className="w-[12%] font-semibold text-muted-foreground">
                Role
              </TableHead>
              <TableHead className="w-[14%] font-semibold text-muted-foreground">
                Phone
              </TableHead>
              <TableHead className="w-[15%] font-semibold text-muted-foreground">
                Sales Team
              </TableHead>
              <TableHead className="w-[15%] font-semibold text-muted-foreground">
                Installer Team
              </TableHead>
              {(canEdit || canDelete) && (
                <TableHead className="w-[10%] font-semibold text-muted-foreground text-right pr-4">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody className="bg-card">
            {filteredUsers.map((user) => {
              const isCurrentUser = user.id === currentUserId;
              const userTeams = user.user_teams || [];
              // SUB-2.6 (FR13). This person is here because they are an Agency
              // Admin of the parent organization. The database refuses any
              // change to the entry from inside this account, so offering the
              // actions would only produce an error the Admin cannot act on.
              const externalGrant = user.external_grant;

              return (
                <TableRow key={user.id} className="hover:bg-muted">
                  <TableCell className="font-medium w-[16%]">
                    <div className="flex items-center overflow-hidden">
                      <span className="text-sm text-foreground truncate">
                        {getUserDisplayName(user)}
                      </span>
                      {isCurrentUser && (
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs border-border text-foreground flex-shrink-0"
                        >
                          You
                        </Badge>
                      )}
                      {externalGrant && (
                        <Badge
                          variant="secondary"
                          className="ml-2 text-xs flex-shrink-0"
                          title={
                            externalGrant.parent_company_name
                              ? `Manages this account on behalf of ${externalGrant.parent_company_name}. Only a Joules superadmin can remove this access.`
                              : "Manages this account on behalf of the parent organization. Only a Joules superadmin can remove this access."
                          }
                        >
                          {externalGrant.parent_company_name
                            ? `via ${externalGrant.parent_company_name}`
                            : "via parent organization"}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-[18%]">
                    <div className="flex items-center text-sm text-foreground overflow-hidden">
                      <Mail className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">
                        {user.email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-[12%]">
                    <span className="text-sm text-foreground truncate block">
                      {roleCellText(user)}
                    </span>
                  </TableCell>
                  <TableCell className="w-[14%]">
                    {user.phone_number ? (
                      <div className="flex items-center text-sm text-foreground overflow-hidden">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">{formatPhoneNumber(user.phone_number)}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-foreground">No phone</span>
                    )}
                  </TableCell>
                  <TableCell className="w-[15%]">
                    <span className="text-sm text-foreground truncate block" title={getUserSalesTeams(user)}>
                      {getUserSalesTeams(user)}
                    </span>
                  </TableCell>
                  <TableCell className="w-[15%]">
                    <span className="text-sm text-foreground truncate block" title={getUserInstallerTeams(user)}>
                      {getUserInstallerTeams(user)}
                    </span>
                  </TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell className="w-[10%] pr-4">
                      <div className="flex justify-end items-center space-x-1">
                        {externalGrant && (
                          <span className="text-xs text-muted-foreground pr-1">
                            Managed by parent
                          </span>
                        )}
                        {/* Superadmin-only Impersonation button */}
                        {hasSuperadminAccess && !externalGrant && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleImpersonate(user)}
                            disabled={user.id === currentUser?.id}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                            title={`Impersonate ${getUserDisplayName(user)}`}
                          >
                            <UserCog className="h-4 w-4" />
                            <span className="sr-only">Impersonate User</span>
                          </Button>
                        )}
                        {canSendAccessEmails && !externalGrant && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResendWelcome(user)}
                            disabled={isCurrentUser || sendingWelcomeTo !== null || !currentTenant?.tenant_id}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                            title={`Resend welcome email to ${user.email}`}
                          >
                            {sendingWelcomeTo === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                            <span className="sr-only">Resend welcome email</span>
                          </Button>
                        )}
                        {canEdit && !externalGrant && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(user)}
                            disabled={isCurrentUser}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"
                            title={`Edit ${getUserDisplayName(user)}`}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit User</span>
                          </Button>
                        )}
                        {canDelete && !externalGrant && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(user)}
                            disabled={isCurrentUser}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-200"
                            title={`Delete ${getUserDisplayName(user)}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete User</span>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
